import { useState, useMemo, useRef, useEffect } from 'react';
import {
  PyThreadContainer,
  PyThreadHeader,
  PyResolveButton,
  PyThreadHeaderTitle,
  PyThreadHeaderCommentsCount,
  PyThreadActions,
  PyThreadActionButton,
  PyThreadHeaderLeftSection,
  PyThreadHeaderRightSection,
  PyThreadExpandIconButton,
  PyThreadDeleteIconButton,
  PyThreadMenuIconButton,
  PyThreadCollapseContainer,
  PyThreadRepliesContainer,
  PyThreadReplyIcon,
  PyThreadExpandReplyIcon,
  PyThreadCollapseReplyIcon,
  PyThreadSecondaryIcon,
} from './styled';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import CheckRounded from '@mui/icons-material/CheckRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import { extractTextFromBody } from '../../helpers/extractTextFromBody';
import { useTranslation } from 'react-i18next';
import { useMultiEditor } from '../../MultiEditorContext';
import { Comment } from './Comment';
import type { ThreadProps } from './types';
import { CustomComposer } from './CustomComposer';
import type { ComposerSubmitComment } from '@liveblocks/react-ui/primitives';
import type { CommentBody, CommentData } from '@liveblocks/client';
import { calculateThreadPosition } from '../../helpers/calculateThreadPosition';
import { parseLiveblocksUser } from '../../helpers/parseLiveblocksUser';
import { useComments } from 'providers/CommentsContext';
import { useThreadPosition } from '../../contexts/ThreadPositionContext';

export const Thread = ({
  thread,
  position,
  isActive,
  onThreadClick,
  onAddComment,
  onResolveThread,
  onMarkActiveThread,
  onEditComment,
  onDeleteComment,
  setActiveThreadId,
  onDeleteThread,
  threadPanelRef,
  totalThreadsInGroup = 1,
  threadIndex,
  isOpenedGroup = false,
  isWithoutEditor = false,
  openedGroups,
}: ThreadProps) => {
  const { t } = useTranslation();
  const [isShownReplies, setIsShownReplies] = useState(true);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { getAllEditors } = useMultiEditor();
  const threadContainerRef = useRef<HTMLDivElement>(null);
  const threadCollapseRef = useRef<HTMLDivElement>(null);

  const { canDeleteComment } = useComments();
  const { getPositionManager } = useThreadPosition();

  const [isAddingComment, setIsAddingComment] = useState(false);

  const threadPosition = useMemo(() => {
    const allEditors = getAllEditors();
    const positionManager = getPositionManager();

    return calculateThreadPosition(
      positionManager,
      thread,
      allEditors,
      threadPanelRef,
      position ?? null,
      totalThreadsInGroup,
      threadIndex,
      isOpenedGroup,
      isWithoutEditor
    );
  }, [
    thread,
    getAllEditors,
    position,
    totalThreadsInGroup,
    threadIndex,
    isOpenedGroup,
    isWithoutEditor,
    threadPanelRef,
    getPositionManager,
    openedGroups,
  ]);

  const threadComments = useMemo(() => {
    return thread.comments.filter((comment) => !comment.deletedAt);
  }, [thread]);

  const handleResolve = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    await onResolveThread(thread.id, thread.__origin);
  };

  const handleMarkActive = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    await onMarkActiveThread(thread.id, thread.__origin);
  };
  const handleDeleteThread = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    await onDeleteThread(thread.id, thread.__origin);
  };

  const handleCancelAddingComment = () => {
    setIsAddingComment(false);
  };

  const handleAddComment = async (comment: ComposerSubmitComment) => {
    if (comment.body) {
      await onAddComment(thread.id, comment.body, thread.__origin);
    }
    setIsAddingComment(false);
  };

  const handleEditComment = (commentId: string, body: CommentBody) => {
    onEditComment(thread.id, commentId, body, thread.__origin);
  };

  const handleDeleteComment = (commentId: string) => {
    onDeleteComment(thread.id, commentId, thread.__origin);
  };

  const handleThreadHeaderClick = () => {
    if (isActive) {
      setActiveThreadId(null);
    } else {
      onThreadClick(thread.id);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleReplyClick = () => {
    setIsAddingComment(true);
    setTimeout(() => {
      threadContainerRef?.current?.scroll({
        top: (threadCollapseRef?.current?.scrollHeight ?? 0) + (threadContainerRef?.current?.clientHeight ?? 0),
        behavior: 'smooth',
      });
    }, 100);
  };

  const mainComment = threadComments[0];

  const getCommentAuthor = (comment: CommentData) => {
    return parseLiveblocksUser(comment.userId).userName || t('comments.anonymous');
  };

  const getThreadHeaderText = () =>
    isActive ? t('comments.thread') : `${getCommentAuthor(mainComment)}: ${extractTextFromBody(mainComment.body)}`;

  useEffect(() => {
    if (isActive) {
      setIsShownReplies(true);
    }
  }, [isActive]);

  return (
    <PyThreadContainer isActive={isActive} top={threadPosition.top} isShown={threadPosition.shown} ref={threadContainerRef}>
      <PyThreadHeader onClick={handleThreadHeaderClick}>
        <PyThreadHeaderLeftSection>
          <PyThreadExpandIconButton size="small">
            {isActive ? <ExpandLess color="primary" /> : <ExpandMore color="primary" />}
          </PyThreadExpandIconButton>
          <PyThreadHeaderTitle>{getThreadHeaderText()}</PyThreadHeaderTitle>
          <PyThreadHeaderCommentsCount>{threadComments.length}</PyThreadHeaderCommentsCount>
        </PyThreadHeaderLeftSection>
        <PyThreadHeaderRightSection>
          {!isWithoutEditor && (
            <>
              {thread.resolved ? (
                <PyResolveButton startIcon={<CheckRounded width={18} height={18} />} onClick={handleMarkActive}>
                  {t('comments.markActive')}
                </PyResolveButton>
              ) : (
                <PyResolveButton startIcon={<CheckRounded width={18} height={18} />} onClick={handleResolve}>
                  {t('comments.resolve')}
                </PyResolveButton>
              )}
            </>
          )}

          {canDeleteComment(mainComment) && (
            <>
              {isActive ? (
                <PyThreadDeleteIconButton size="small" onClick={handleDeleteThread}>
                  <PyThreadSecondaryIcon>
                    <DeleteOutlineRounded />
                  </PyThreadSecondaryIcon>
                </PyThreadDeleteIconButton>
              ) : (
                <PyThreadMenuIconButton
                  size="small"
                  onClick={handleMenuOpen}
                  aria-controls={menuAnchorEl ? 'thread-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuAnchorEl ? 'true' : undefined}
                >
                  <PyThreadSecondaryIcon>
                    <MoreHorizRounded />
                  </PyThreadSecondaryIcon>
                </PyThreadMenuIconButton>
              )}

              <Menu
                id="thread-menu"
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'thread-menu-button',
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={handleDeleteThread}>
                  <ListItemText>{t('comments.delete')}</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </PyThreadHeaderRightSection>
      </PyThreadHeader>

      <Collapse in={isActive} sx={{ width: '100%' }} ref={threadCollapseRef}>
        <PyThreadCollapseContainer isActive={isActive}>
          <Box>
            <Comment comment={mainComment} mainComment onEditComment={handleEditComment} roomType={thread.__origin} />

            <PyThreadActions>
              <PyThreadActionButton startIcon={<PyThreadReplyIcon />} onClick={handleReplyClick}>
                {t('comments.reply')}
              </PyThreadActionButton>
              {threadComments.length > 1 && (
                <PyThreadActionButton onClick={() => setIsShownReplies(!isShownReplies)}>
                  {isShownReplies ? <PyThreadExpandReplyIcon /> : <PyThreadCollapseReplyIcon />}
                  {threadComments.length - 1} {t('comments.replies')}
                </PyThreadActionButton>
              )}
            </PyThreadActions>

            {isShownReplies && (
              <PyThreadRepliesContainer>
                {threadComments.slice(1).map((comment) => (
                  <Comment
                    key={comment.id}
                    comment={comment}
                    onEditComment={handleEditComment}
                    onDeleteComment={handleDeleteComment}
                    roomType={thread.__origin}
                  />
                ))}
              </PyThreadRepliesContainer>
            )}

            {isAddingComment && (
              <CustomComposer
                roomType={thread.__origin}
                onCancel={handleCancelAddingComment}
                onSubmit={handleAddComment}
                placeholder={t('comments.enterComment')}
                submitLabel={t('comments.comment')}
                cancelLabel={t('comments.cancel')}
              />
            )}
          </Box>
        </PyThreadCollapseContainer>
      </Collapse>
    </PyThreadContainer>
  );
};
